import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { 
  MessageSquare, 
  X, 
  Send, 
  User, 
  Bot, 
  ChevronRight, 
  Phone, 
  HelpCircle,
  ExternalLink
} from "lucide-react";
import { getChatbotResponse } from "../services/chatbotService";
import { SOLUTIONS } from "../constants";

type Message = {
  id: string;
  role: "user" | "model";
  text: string;
  timestamp: number;
};

const FAQ_ITEMS = [
  { q: "What solutions do you offer?", a: "We offer ERP, CRM, RSS, File Management, Analytics, Project Management, and Support solutions." },
  { q: "What is your pricing?", a: "Our core package starts at $15/mo, with enterprise add-ons available for +$5/mo per additional module." },
  { q: "Are the solutions white-labeled?", a: "Yes, all our tools are built to be custom-branded as your own enterprise software." },
  { q: "How do I get a demo?", a: "You can click on 'Live Chat' to speak with an agent or visit our Contact page." },
];

export const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      role: "model",
      text: "Hi there! 👋 I'm your ecosystem assistant. How can I help you today?",
      timestamp: Date.now(),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showFAQ, setShowFAQ] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      text,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setIsLoading(true);
    setShowFAQ(false);

    // Prepare history for Gemini
    const history = messages.map(m => ({
      role: m.role,
      parts: [{ text: m.text }]
    }));
    history.push({ role: "user", parts: [{ text }] });

    const responseText = await getChatbotResponse(history);

    const botMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: "model",
      text: responseText || "I'm sorry, I couldn't process that.",
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, botMessage]);
    setIsLoading(false);
  };

  const handleFAQClick = (q: string) => {
    handleSendMessage(q);
  };

  const handleLiveChat = () => {
    const whatsappNumber = "1234567890"; // Placeholder - would be user's business number
    const message = encodeURIComponent("Hi, I was using the chatbot and I'd like to speak with a human agent for support.");
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, "_blank");
    
    // Add a message in the chat as well
    const botMsg: Message = {
      id: Date.now().toString(),
      role: "model",
      text: "I've opened a WhatsApp chat for you with one of our support agents! You can continue the conversation there.",
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, botMsg]);
  };

  return (
    <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="mt-6 w-[calc(100vw-2rem)] sm:mt-0 sm:w-[350px] md:w-[400px] h-[calc(100vh-10rem)] sm:h-[calc(100vh-8rem)] min-h-[400px] max-h-[600px] bg-[#0A0A0A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-4 bg-orange-500 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-white text-sm">Assistant</h4>
                  <p className="text-[10px] text-white/70 uppercase tracking-widest">Always Online</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                id="close-chatbot"
                aria-label="Close chatbot"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, x: msg.role === "user" ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`flex gap-2 max-w-[80%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    <div className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center ${msg.role === "user" ? "bg-orange-500" : "bg-white/10"}`}>
                      {msg.role === "user" ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
                    </div>
                    <div className={`p-3 rounded-2xl text-sm ${msg.role === "user" ? "bg-orange-500 text-white rounded-tr-none" : "bg-white/5 text-white/90 border border-white/10 rounded-tl-none"}`}>
                      <div className={`prose prose-sm max-w-none prose-p:leading-relaxed prose-pre:bg-white/5 prose-pre:border prose-pre:border-white/10 prose-p:my-0 first:prose-p:mt-0 last:prose-p:mb-0 ${msg.role === "user" ? "prose-invert" : "prose-invert text-white/90"}`}>
                        <Markdown remarkPlugins={[remarkGfm]}>
                          {msg.text}
                        </Markdown>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white/5 p-3 rounded-2xl rounded-tl-none border border-white/10 space-x-1 flex items-center">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-bounce" />
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-bounce [animation-delay:0.2s]" />
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-bounce [animation-delay:0.4s]" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions / FAQ */}
            <AnimatePresence>
              {showFAQ && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="px-4 pb-2"
                >
                  <div className="flex items-center gap-2 mb-2 text-[10px] text-white/30 uppercase tracking-widest font-bold">
                    <HelpCircle className="w-3 h-3" />
                    Frequently Asked
                  </div>
                  <div className="grid grid-cols-1 gap-2 mb-2">
                    {FAQ_ITEMS.slice(0, 3).map((item, idx) => (
                      <button
                        key={idx}
                        id={`faq-item-${idx}`}
                        onClick={() => handleFAQClick(item.q)}
                        aria-label={`Ask FAQ: ${item.q}`}
                        className="text-left p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-orange-500/50 transition-all text-xs text-white/70 flex items-center justify-between group"
                      >
                        {item.q}
                        <ChevronRight className="w-3 h-3 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer / Input */}
            <div className="p-4 border-t border-white/10 bg-white/[0.02]">
              <div className="flex gap-2 mb-3">
                <button 
                  id="faq-toggle-button"
                  onClick={() => setShowFAQ(!showFAQ)}
                  aria-label="Toggle FAQ"
                  className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[10px] font-bold uppercase tracking-widest text-white/50 border border-white/10 transition-all flex items-center gap-1"
                >
                  <HelpCircle className="w-3 h-3" /> FAQ
                </button>
                <button 
                  id="whatsapp-live-chat-button"
                  onClick={handleLiveChat}
                  aria-label="Start Live Chat on WhatsApp"
                  className="px-3 py-1.5 rounded-full bg-green-500/10 hover:bg-green-500/20 text-[10px] font-bold uppercase tracking-widest text-green-500 border border-green-500/20 transition-all flex items-center gap-1"
                >
                  <Phone className="w-3 h-3" /> Live Chat
                </button>
              </div>
              <form 
                id="chatbot-input-form"
                onSubmit={(e) => { e.preventDefault(); handleSendMessage(inputText); }}
                className="flex gap-2"
              >
                <input
                  id="chatbot-message-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-orange-500/50 text-white"
                />
                <button 
                  id="chatbot-send-button"
                  type="submit"
                  disabled={!inputText.trim() || isLoading}
                  aria-label="Send message"
                  className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center disabled:opacity-50 disabled:grayscale transition-all hover:scale-105 active:scale-95"
                >
                  <Send className="w-5 h-5 text-white" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/20 text-white relative group ${isOpen ? 'hidden sm:flex' : ''}`}
        id="toggle-chatbot"
        aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ opacity: 0, rotate: 90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: -90 }}
            >
              <MessageSquare className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
        {!isOpen && (
          <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full border-4 border-[#050505] animate-pulse" />
        )}
      </motion.button>
    </div>
  );
};
